import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Collapse from '@mui/material/Collapse'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Zoom from '@mui/material/Zoom'
import { useQueryClient } from '@tanstack/react-query'
import { FormikButtons } from 'components/Formik/FormikButtons'
import { FormikCheckboxGroup } from 'components/Formik/FormikCheckboxGroup'
import { Form, Formik } from 'formik'
import { TwoFAVerifyApp } from 'pages/MyAccountEdit/TwoFAVerifyApp'
import { logout } from 'utils/api/apiClient'
import userQueryKeys from 'utils/api/User/userQueryKeys'
import * as Yup from 'yup'

import { useGetQRCode } from './hooks/useGetQRCode'
import { usePatchTwoFASetting } from './hooks/usePatchTwoFASetting'
import { TwoFAVerifyEmail } from './TwoFAVerifyEmail'
import { TwoFAType, TwoFATypes } from './types'

export const TwoFASettingPage = () => {
  const { t } = useTranslation()
  const [openVerify, setOpenVerify] = useState<TwoFATypes | null>(null)
  const [confirmedTypes, setConfirmedTypes] = useState<TwoFATypes[]>([])
  const [qrCode, setQrCode] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const { mutate, isLoading: isPending } = usePatchTwoFASetting(() => {
    queryClient.invalidateQueries({ queryKey: userQueryKeys.me() })
  })
  const { mutateAsync: getQRCode, isLoading: isPendingQRCode } = useGetQRCode()

  const initialValues: { type: TwoFATypes[] } = {
    type: [],
  }

  const validationSchema = Yup.object().shape({
    type: Yup.array(Yup.number()).required().min(1),
  })

  const onSubmit = async (values: typeof initialValues) => {
    mutate({ type: confirmedTypes })
  }

  const set = async (value: TwoFATypes) => {
    setOpenVerify(value)
    if (value === TwoFAType.googleAuthenticator) {
      const responseCode = await getQRCode()
      setQrCode(responseCode?.data?.qrCode)
    }
  }

  return (
    <Box
      display="grid"
      height="100vh"
      width="100vw"
      justifyContent="center"
      alignItems="center"
      sx={{ background: (theme) => theme.palette.background.default }}
    >
      <Grid container direction="row" justifyContent="center">
        <Grid item xs={12} md={8} lg={6} xl={4}>
          <Zoom in>
            <Paper elevation={24} sx={{ p: 5 }}>
              <Collapse in={openVerify === null}>
                <Formik initialValues={initialValues} enableReinitialize onSubmit={onSubmit}>
                  <Form>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="h3" fontWeight="bold" align="center">
                          {t('profile.two_factor_authentication.login.title')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body1">{t('profile.two_factor_authentication.description')}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ ml: -0.5 }}>
                          <FormikCheckboxGroup
                            name="type"
                            label={'profile.two_factor_authentication.choose'}
                            labelProps={{
                              sx: { mr: 0, fontSize: (theme) => theme.typography.body1.fontSize },
                            }}
                            controlLabels={[
                              {
                                label: `profile.two_factor_authentication.${TwoFAType.googleAuthenticator}`,
                                description: 'profile.two_factor_authentication.app',
                                value: TwoFAType.googleAuthenticator,
                              },
                              {
                                label: `profile.two_factor_authentication.${TwoFAType.email}`,
                                value: TwoFAType.email,
                                description: 'profile.two_factor_authentication.email',
                              },
                            ]}
                            value={confirmedTypes}
                            onChange={(value, checked) => {
                              if (checked) {
                                set(value)
                              } else {
                                set(null)
                                setConfirmedTypes((prev) => prev.filter((type) => type !== value))
                              }
                            }}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body1">
                          {t('profile.two_factor_authentication.login.description2')}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <FormikButtons
                          isLoading={isPending}
                          textButtonCancel="app.logout"
                          onBack={() => logout()}
                          disabled={confirmedTypes?.length < 1}
                        />
                      </Grid>
                    </Grid>
                  </Form>
                </Formik>
              </Collapse>
              <Collapse in={openVerify === TwoFAType.googleAuthenticator} mountOnEnter unmountOnExit>
                <TwoFAVerifyApp
                  qrCode={qrCode}
                  onBack={() => setOpenVerify(null)}
                  onSuccess={() => {
                    setConfirmedTypes((prev) => [
                      ...prev.filter((type) => type !== TwoFAType.googleAuthenticator),
                      TwoFAType.googleAuthenticator,
                    ])
                    setOpenVerify(null)
                  }}
                />
              </Collapse>
              <Collapse in={openVerify === TwoFAType.email} mountOnEnter unmountOnExit>
                <TwoFAVerifyEmail
                  onBack={() => setOpenVerify(null)}
                  onSuccess={() => {
                    setConfirmedTypes((prev) => [...prev.filter((type) => type !== TwoFAType.email), TwoFAType.email])
                    setOpenVerify(null)
                  }}
                />
              </Collapse>
              {isPendingQRCode && (
                <Box display="grid" justifyContent="center">
                  <CircularProgress size={64} />
                </Box>
              )}
            </Paper>
          </Zoom>
        </Grid>
      </Grid>
    </Box>
  )
}
